import { faArrowLeft, faSearch } from '@fortawesome/pro-light-svg-icons';
import { Dispatch, SetStateAction } from 'react';
import { tv } from 'tailwind-variants';

import { useStickyHeader } from '@/app/(main)/_components/search-modal/_hooks/use-sticky-header.hook';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { SheetHeader } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { SelectMoreResults } from '@/app/(main)/_components/search-modal/_components/select-more-results.component';
import { useSelectMoreExams } from '@/app/(main)/_components/search-modal/_hooks/use-select-more-exams.hook';
import { Typography } from '@/components/ui/typography/typography.component';
import { InfiniteScroll } from '@/components/ui/infinite-scroll.component';

const selectedMoreExamsStyle = tv({
  slots: {
    seeMoreContainer: [
      'data-[show=false]:hidden data-[show=true]:opacity-1',
      'data-[show=false]:animate-fade-out-bottom-procedure data-[show=true]:animate-fade-in-bottom-procedure',
      'transition-all duration-300 rounded-b-md md:max-w-[800px] w-full bg-white',
      'flex justify-between items-center text-center p-5',
      'self-end',
    ],
  },
  variants: {
    isOnMobileDevice: {
      true: {
        seeMoreContainer: 'pt-[-50px]',
      },
    },
  },
});

export interface SelectMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  firstProcedureSelected: SelectedProcedures;
  onClear: () => void;
  setSelectProcedures: Dispatch<SetStateAction<SelectedProcedures[]>>;
}

export function SelectMoreExams({
  selectedProcedures,
  firstProcedureSelected,
  onClear,
  setSelectProcedures,
}: SelectMoreExamsProps) {
  const { handleJump: jump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const { seeMoreContainer } = selectedMoreExamsStyle();
  const { containerRef, headerCallbackRef, isScrolled } = useStickyHeader();

  const hasExtraSelectedProcedures
    = selectedProcedures.filter((item) => item.id !== firstProcedureSelected.id).length > 0;

  const handleJump = (key: ProgressiveSearchModalKeys, returning = false) => {
    if (!jump) {
      return;
    }

    if (hasExtraSelectedProcedures) {
      jump({ key: ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, returning: true });
      return;
    }

    jump({ key, returning });

    if (returning && onClear && key !== ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE) {
      onClear();
    }
  };

  const {
    searchDisplayValue,
    handleSearchInput,
    inputRef,
    searchResults,
    emptySearchResults,
    isLoadingLaboratoryProcedures,
    isLoadingMoreLaboratoryProcedures,
    handleSelectProcedure,
    isChecked,
    handleLoadMore,
    hasSearchResults,
    isSearching,
    hasNoMoreResults,
    showSeeSchedules,
    searchInputPlaceholder,
    addMoreExamsMessage,
    selectionExamsMessage,
    selectedExamsCount,
    searchType,
  } = useSelectMoreExams({
    selectedProcedures,
    setSelectProcedures,
  });

  return (
    <div
      ref={headerCallbackRef}
      className="flex flex-col h-full"
    >
      <div
        className={`
          pt-5 px-6
          sticky top-0 left-0 right-0 z-20
          transition-colors duration-200
          ${isScrolled ? 'bg-gray-50 shadow-floating pb-3' : 'bg-white'}
        `}
      >
        <SheetHeader
          className="flex flex-row items-start gap-4 mb-4"
        >
          <button
            data-testid="back-button--select-more-exams"
            className="pt-5"
            onClick={() => {
              if (selectedProcedures.length === 0) {
                handleJump(ProgressiveSearchModalKeys.SEARCH, true);
                return;
              }
              handleJump(ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, true);
            }}
          >
            <Icon icon={faArrowLeft} size="xl" className="text-gray-500 self-center cursor-pointer" />
          </button>
          <Input
            data-testid="search-input--select-more-exams"
            placeholder={searchInputPlaceholder}
            startIcon={faSearch}
            startIconStyle="text-gray-500 text-xl"
            value={searchDisplayValue}
            onChange={handleSearchInput}
            ref={inputRef}
            className="w-full"
          />
        </SheetHeader>

        {!hasSearchResults && !isSearching && (
          <div className="mt-1">
            {isChecked(firstProcedureSelected)
              ? (
                  <Typography data-testid="select-procedure-paragraph--select-more-exams">
                    {addMoreExamsMessage}
                  </Typography>
                )
              : (
                  <Typography data-testid="select-procedure-paragraph--select-more-exams">
                    {selectionExamsMessage}
                  </Typography>
                )}
          </div>
        )}

        {emptySearchResults && (
          <Typography
            className="mt-6 text-left"
            data-testid="search-empty-results--select-more-exams"
          >
            Não encontramos resultados para
            {' '}
            <strong>{searchDisplayValue}</strong>
            . Tente outra palavra.
          </Typography>
        )}
      </div>

      {!emptySearchResults && (
        <>
          <div ref={containerRef} className="flex flex-col md:basis-[80vh] overflow-y-scroll h-full">
            <SelectMoreResults
              isLoading={isLoadingLaboratoryProcedures}
              searchResults={searchResults}
              searchType={searchType}
              handleSelectProcedure={handleSelectProcedure}
              isChecked={isChecked}
            />

            {hasSearchResults && !isLoadingLaboratoryProcedures && (
              <InfiniteScroll
                hasMore={!hasNoMoreResults}
                isLoading={isLoadingMoreLaboratoryProcedures}
                onLoadMore={handleLoadMore}
              />
            )}
          </div>
        </>
      )}

      <div
        data-testid="see-more-container--select-more-exams"
        data-show={selectedProcedures.length > 0 && showSeeSchedules}
        className={seeMoreContainer({
          isOnMobileDevice: showSeeSchedules && selectedProcedures.length > 0,
        })}
      >
        <Typography>{selectedExamsCount}</Typography>
        <Button
          data-testid="see-schedules-button--select-more-exams"
          className="w-fit"
          onClick={() => jump({ key: ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS })}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
